import { QueryErrorBoundary } from "@components/common/errors";
import { StatusBadge, type StatusConfig } from "@components/display/StatusBadge";
import {
  Briefcase,
  Building2,
  Calendar,
  FileText,
  Mail,
  MapPin,
  Phone,
  User,
  Users,
} from "lucide-react";
import { SkeletonBase } from "@/components/common/skeletons/SkeletonBase";
import { Avatar, getInitials } from "@/components/ui/Avatar";
import { useDashboardSummary } from "../hooks/useDashboardSummary";

/**
 * Person operating state (backend DashboardSummaryProvider: isArchive() ?
 * 'Inactif' : 'Actif') → L2.5 tokens (active / archived mirror the legacy
 * success / ghost color pair).
 */
const STATUT_PERSONNE = {
  Actif: { code: "Actif", label: "Actif", token: "active" },
  Inactif: { code: "Inactif", label: "Inactif", token: "archived" },
} as const satisfies Record<string, StatusConfig>;

export function DashboardProfileView() {
  const { data, isLoading, error, refetch } = useDashboardSummary();

  if (isLoading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return (
      <QueryErrorBoundary
        error={error}
        refetch={refetch}
        className="my-4"
        title="Erreur du chargement du profil"
      />
    );
  }

  const profile = data?.profile || {};
  const fullName = data?.userFullName || "Utilisateur";
  const jobTitle = profile.poste || "Non renseigné";

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
      {/* Profile Header Card - Full Width */}
      <div className="card bg-base-200 shadow-sm border border-base-300/60 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md">
        <div className="card-body p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
            {/* Avatar */}
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary to-secondary opacity-20 blur-lg"></div>
              <Avatar
                fallback={getInitials(fullName)}
                size="lg"
                className="relative border-2 border-primary/20 shadow-primary/20"
              />
            </div>

            {/* Name & Title */}
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-base-content tracking-tight">
                {fullName}
              </h2>
              <p className="text-base text-base-content/60 mt-1">{jobTitle}</p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-3">
                <span className="text-sm text-base-content/60">
                  Matricule:{" "}
                  <span className="font-semibold text-base-content">
                    {profile.matricule || "—"}
                  </span>
                </span>
                <span className="divider divider-horizontal mx-1 h-4"></span>
                {profile.statut ? (
                  <StatusBadge
                    status={profile.statut}
                    config={STATUT_PERSONNE}
                    size="sm"
                    className="px-3 py-2 font-medium"
                  />
                ) : (
                  <span className="px-3 py-2 text-sm font-medium text-base-content/60">—</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {/* Contact Card */}
        <div className="card bg-base-200 shadow-sm border border-base-300/60 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md hover:border-info/30 group">
          <div className="card-body p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-info" />
              </div>
              <h3 className="text-base font-bold text-base-content">Coordonnées</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4 min-w-0 p-3 rounded-lg hover:bg-info/5 transition-colors duration-200">
                <div className="w-9 h-9 rounded-lg bg-info/10 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-info" />
                </div>
                <span className="text-sm text-base-content/80 truncate min-w-0">
                  {profile.email || "—"}
                </span>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-info/5 transition-colors duration-200">
                <div className="w-9 h-9 rounded-lg bg-info/10 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-info" />
                </div>
                <span className="text-sm text-base-content/80">{profile.telephone || "—"}</span>
              </div>
              <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-info/5 transition-colors duration-200">
                <div className="w-9 h-9 rounded-lg bg-info/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-info" />
                </div>
                <span className="text-sm text-base-content/80 leading-relaxed">
                  {profile.adresse || "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Info Card */}
        <div className="card bg-base-200 shadow-sm border border-base-300/60 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md hover:border-primary/30">
          <div className="card-body p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-base-content">Informations Professionnelles</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <ProfileField icon={Building2} label="Site / Atelier" value={profile.site} />
              <ProfileField icon={Briefcase} label="Poste" value={profile.poste} />
              <ProfileField icon={Users} label="Service" value={profile.service} />
              <ProfileField icon={User} label="Responsable" value={profile.responsable} />
              <ProfileField
                icon={Calendar}
                label="Date d'embauche"
                value={profile.dateEmbauche ? formatDate(profile.dateEmauche) : null}
              />
              <ProfileField icon={FileText} label="Type de contrat" value={profile.typeContrat} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileField({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value?: string | null;
}) {
  return (
    <div className="border-b border-base-200/60 pb-3 pt-2 hover:bg-base-200/30 transition-colors duration-200 rounded-lg -mx-2 px-2">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-base-content/40" />}
          <span className="text-sm text-base-content/60">{label}</span>
        </div>
        <span className="text-sm font-medium text-base-content text-right">{value || "—"}</span>
      </div>
    </div>
  );
}

function formatDate(dateString: string) {
  if (!dateString) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
      {/* Profile Header Skeleton */}
      <div className="card bg-base-200 shadow-sm border border-base-300/60 rounded-xl overflow-hidden">
        <div className="card-body p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
            <SkeletonBase variant="circle" className="w-24 h-24" />
            <div className="flex-1 text-center md:text-left">
              <SkeletonBase variant="line" className="h-8 w-48 mx-auto md:mx-0 mb-2" />
              <SkeletonBase variant="line" className="h-5 w-32 mx-auto md:mx-0" />
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-3">
                <SkeletonBase variant="line" className="h-4 w-24" />
                <SkeletonBase variant="block" className="h-6 w-16 rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards Grid Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        {/* Contact Card Skeleton */}
        <div className="card bg-base-200 shadow-sm border border-base-300/60 rounded-xl">
          <div className="card-body p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <SkeletonBase variant="block" className="w-10 h-10 rounded-xl" />
              <SkeletonBase variant="line" className="h-5 w-28" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <SkeletonBase variant="block" className="w-9 h-9 rounded-lg" />
                <SkeletonBase variant="line" className="h-4 flex-1" />
              </div>
              <div className="flex items-center gap-4">
                <SkeletonBase variant="block" className="w-9 h-9 rounded-lg" />
                <SkeletonBase variant="line" className="h-4 w-32" />
              </div>
              <div className="flex items-start gap-4">
                <SkeletonBase variant="block" className="w-9 h-9 rounded-lg" />
                <SkeletonBase variant="line" className="h-4 flex-1" />
              </div>
            </div>
          </div>
        </div>

        {/* Professional Info Card Skeleton */}
        <div className="card bg-base-200 shadow-sm border border-base-300/60 rounded-xl">
          <div className="card-body p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <SkeletonBase variant="block" className="w-10 h-10 rounded-xl" />
              <SkeletonBase variant="line" className="h-5 w-52" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {[...Array(6)].map((_, i) => (
                <div key={i}>
                  <SkeletonBase variant="line" className="h-4 w-24 mb-1" />
                  <SkeletonBase variant="line" className="h-4 w-28" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
